<template>
  <div class="space-y-1">
    <label v-if="label"  class="block text-[12px] font-medium text-gray-700 mb-1 flex items-center">
      <span v-if="required" class="text-red-500 ml-1">*</span>
      {{ label }}
      <span v-if="rate" class="text-blue-800 dark:text-blue-300 text-[10px] mr-2">
        (× {{rate | toMoney(0)}} )
      </span>
    </label>
    <div class="relative rounded-md shadow-sm">
      <input
          :disabled="disabled"
          inputmode="decimal"
          dir="ltr"
          :placeholder="placeholder"
          @input="change"
          v-model="formattedAmount"
          :class="[
            `px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-indigo-500
             text-[13px] text-right pr-10 transition-all duration-200`,
            bgColor,
            { 'border-red-300 dark:border-red-500/50': error },
            { 'text-[8px]': formattedAmount.length > 14 },
            { 'cursor-not-allowed opacity-70 ': disabled }
          ]"
          autocomplete="off"
          v-bind="$attrs"
      >
      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <span class="text-gray-500 dark:text-gray-400 text-[8px]">{{ currencyUnit }}</span>
      </div>
    </div>
    <label v-if="labelDown" class="block mb-1 pt-2 flex items-center">
      <span class="text-gray-800 dark:text-gray-300 text-[10px]">
         {{ labelDown }}
      </span>
      <span v-if="labelDownValue" class="text-blue-800  text-[8px] mr-1">
        ( {{ labelDownValue | toMoney }} تومان)
      </span>
    </label>

    <p v-if="error" class="mt-1 text-xs text-red-600 dark:text-red-400">{{ error }}</p>
  </div>
</template>

<script>
export default {
  name: "CleaveMoney",
  props: {
    bgColor: {
      type: String,
      default: "bg-blue-50/80 dark:bg-slate-800/70 w-full border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
    },
    value: {},
    decimalScale: {
      default: 6,
    },
    label: {
      type: String,
      default: "قیمت"
    },
    rate: {
      default: null
    },
    labelDown: {
      default: null
    },
    labelDownValue: {
      default: null
    },
    placeholder: {
      default: null,
    },
    absoluteValue: {
      default: false,
    },
    disabled: {
      default: false,
    },
    currencyUnit: {
      type: String,
      default: 'تومان'
    },
    required: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      formattedAmount: "",
    };
  },
  mounted() {
    this.setAmount(this.value);
  },
  watch: {
    value() {
      if (this.value != this.getAmount()) this.setAmount(this.value);
    },
  },
  methods: {
    setAmount(value) {
      if (this.absoluteValue && value < 0) value = -value;

      value = (value === null || value === undefined) ? "" : String(value);

      let raw = value.replace(/,/g, "").replace(/[^0-9.]/g, "");

      const firstDot = raw.indexOf(".");
      if (firstDot !== -1) {
        raw = raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, "");
      }

      const [intPart = "", decPart = ""] = raw.split(".");
      const limitedDec = decPart.slice(0, this.decimalScale);

      const intClean = intPart === "" ? "" : String(+intPart);
      const intWithCommas = intClean.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

      this.formattedAmount = raw.includes(".")
          ? `${intWithCommas}.${limitedDec}`
          : intWithCommas;
    },
    getAmount() {
      let valueParts = this.formattedAmount
          .split(",")
          .join("")
          .split(".");
      if (valueParts.length == 2) {
        return `${valueParts[0]}.${valueParts[1].substr(0, 6)}`;
      }
      let value = valueParts[0];
      if (value == "") {
        return null;
      } else {
        return +value;
      }
    },
    change(event) {
      this.setAmount(event.target.value);
      this.$emit("input", this.getAmount());
    },
  },
};
</script>

<style scoped>
/* Dark mode styles for the component */
:deep(input) {
  background: rgba(30, 41, 59, 0.7) !important;
  color: rgba(226, 232, 240, 1) !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  transition: all 0.2s ease;
}

:deep(input:focus) {
  outline: none;
  border-color: rgba(99, 102, 241, 0.5) !important;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.25) !important;
}

:deep(input:disabled) {
  background: rgba(30, 41, 59, 0.9) !important;
  color: #000000;
  cursor: not-allowed;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* Placeholder color */
:deep(input::placeholder) {
  color: rgba(148, 163, 184, 0.5) !important;
}

/* Currency unit styling */
.absolute.inset-y-0.left-0.pl-3.flex.items-center.pointer-events-none span {
  color: rgba(148, 163, 184, 0.7) !important;
}

/* Error state */
.border-red-300 {
  border-color: rgba(248, 113, 113, 0.5) !important;
}

.dark .border-red-300 {
  border-color: rgba(248, 113, 113, 0.3) !important;
}

/* Focus ring color */
.focus\:ring-blue-500:focus {
  --tw-ring-color: rgba(59, 130, 246, 0.5) !important;
}

.dark .focus\:ring-indigo-500:focus {
  --tw-ring-color: rgba(99, 102, 241, 0.5) !important;
}

/* Hover effects */
:deep(input:not(:disabled)):hover {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

/* Animation for value changes */
.formattedAmount {
  transition: color 0.2s ease;
}

/* Label colors */
.text-gray-700 {
  color: rgba(226, 232, 240, 0.9) !important;
}

.text-blue-800 {
  color: rgba(96, 165, 250, 0.9) !important;
}

.text-gray-800 {
  color: rgba(226, 232, 240, 0.8) !important;
}

/* Error message */
.text-red-600 {
  color: rgba(248, 113, 113, 0.9) !important;
}

/* For very long numbers */
.text-\[8px\] {
  color: rgba(226, 232, 240, 0.9) !important;
}

/* Glass morphism effect for dark mode */
.bg-slate-800\/70 {
  background: rgba(30, 41, 59, 0.7) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/* Border in dark mode */
.border-gray-600 {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

/* Text colors in dark mode */
.dark\:text-gray-100 {
  color: rgba(226, 232, 240, 1) !important;
}

.dark\:text-gray-300 {
  color: rgba(203, 213, 225, 1) !important;
}

.dark\:text-blue-300 {
  color: rgba(147, 197, 253, 1) !important;
}

.dark\:text-red-400 {
  color: rgba(248, 113, 113, 1) !important;
}

/* Shadow adjustments for dark mode */
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.dark .shadow-sm {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
}

/* Gradient border effect on focus */
:deep(input:focus) {
  background: linear-gradient(
      rgba(30, 41, 59, 0.7),
      rgba(30, 41, 59, 0.7)
  ),
  linear-gradient(
      90deg,
      rgba(99, 102, 241, 0.1),
      rgba(168, 85, 247, 0.1)
  ) !important;
  background-origin: border-box !important;
  background-clip: padding-box, border-box !important;
  border: 1px solid transparent !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  :deep(input) {
    font-size: 14px !important;
    padding: 8px 12px !important;
  }

  .text-\[8px\] {
    font-size: 7px !important;
  }
}

/* Print styles */
@media print {
  :deep(input) {
    background: white !important;
    color: black !important;
    border: 1px solid #ccc !important;
  }
}
</style>